import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;
const EE_ADD_TO_CART_EVENT_NAME = 'ee_add_to_cart';

/**
 * @param {string | undefined} source
 * @returns {'nosto' | 'static' | null}
 */
function normalizeDrawerSource(source) {
  if (source === 'nosto') return 'nosto';
  if (source === 'static') return 'static';
  return null;
}

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    if (this.refs.addToCartButton?.getAttribute('data-action') === 'back-in-stock') return;
    this.animateAddToCart();

    if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #isSubmitting = false;

  #isCartDrawerForm() {
    return Boolean(this.closest('cart-drawer-component') || this.closest('quick-add-dialog#quick-add-dialog-cart'));
  }

  /**
   * Tracks drawer recommendation add-to-cart for GA4/GTM.
   * Fires only for cart-drawer recommendation sources: nosto/static.
   * Mirrors the org's standard add_to_cart dataLayer shape (see references/WildRoman,
   * references/Supergut), with only the event name and a `recommendation_source` param
   * differing (named to avoid colliding with this container's existing `source` param).
   *
   * @param {FormData} formData
   * @param {Record<string, any>} responseData - The /cart/add.js response body for the added line item.
   */
  #trackEeAddToCart(formData, responseData = {}) {
    if (!this.#isCartDrawerForm()) return;

    const quickAddDialog = this.closest('quick-add-dialog');
    const source =
      normalizeDrawerSource(this.dataset.source) ||
      normalizeDrawerSource(quickAddDialog?.getAttribute('data-source') || undefined);

    if (!source) return;

    const variantId = formData.get('id')?.toString() || '';
    const quantity = Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    const productId = String(responseData.product_id || this.dataset.productId || '');
    const sku = String(responseData.sku || '').trim();
    const productName =
      String(responseData.product_title || '').trim() ||
      quickAddDialog?.querySelector('.view-product-title a')?.textContent?.trim() ||
      '';
    const variantName = String(responseData.variant_title || '').trim();
    const vendor = String(responseData.vendor || '').trim();
    const productType = String(responseData.product_type || '').trim();
    const sellingPlanId = responseData.selling_plan_allocation?.selling_plan?.id || null;

    // Unit price actually charged (already reflects any product-level markdown);
    // intentionally not final_price/final_line_price, which can additionally reflect
    // cart-level discounts and would make this event's value non-comparable to the
    // org's other add_to_cart implementations.
    const unitPriceCents = Number(responseData.price) || 0;

    const currencyCode = String(window.Shopify?.currency?.active || '').toUpperCase();
    const currency = currencyCode || 'EGP';
    const unitPrice = Number((unitPriceCents / 100).toFixed(2));
    const cartValue = Number((unitPrice * quantity).toFixed(2));

    const win = /** @type {Window & { dataLayer?: Array<Record<string, unknown>> }} */ (window);
    win.dataLayer = win.dataLayer || [];
    win.dataLayer.push({ ecommerce: null });
    win.dataLayer.push({
      event: EE_ADD_TO_CART_EVENT_NAME,
      // Pushed as `recommendation_source` (not `source`) -- this GTM container already
      // populates a `source` event parameter from UTM/traffic-acquisition data on most
      // events, and reusing that name collides with it (confirmed empirically: the
      // "source" custom dimension showed UTM values like google/bing/criteo, not
      // nosto/static). Do not rename this back to `source`.
      recommendation_source: source,
      ecommerce: {
        currency,
        value: cartValue,
        cart_value_text: cartValue.toFixed(2),
        selling_plan_id: sellingPlanId,
        items: [
          {
            item_id: sku || variantId || productId,
            item_name: productName || undefined,
            item_brand: vendor || undefined,
            item_category: productType || undefined,
            item_variant: variantName || undefined,
            price: unitPrice,
            quantity,
            purchase_type: sellingPlanId ? 'subscription' : 'one-time',
            selling_plan_id: sellingPlanId,
          },
        ],
      },
    });
  }

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    // Safari may ignore dynamic type="button", so Back in Stock can still trigger submit — open modal instead
    if (event.submitter?.getAttribute('data-action') === 'back-in-stock') {
      const dialog =
        document.getElementById('back-in-stock-dialog') ||
        document.querySelector('.back-in-stock-dialog');
      if (dialog && typeof dialog.showDialog === 'function') {
        dialog.showDialog();
        const variantSelect =
          document.querySelector('#back-in-stock-dialog [data-variant-selector]') ||
          document.querySelector('.back-in-stock-dialog [data-variant-selector]');
        if (this.refs.variantId && variantSelect) variantSelect.value = this.refs.variantId.value;
      }
      return;
    }

    // Prevent multiple simultaneous submissions
    if (this.#isSubmitting) {
      console.warn('ProductFormComponent: Already submitting, ignoring duplicate submit');
      return;
    }

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    // Check form-level submission flag
    if (form.hasAttribute('data-submitting')) {
      console.warn('ProductFormComponent: Form already submitting, ignoring');
      return;
    }

    // Mark as submitting
    this.#isSubmitting = true;
    form.setAttribute('data-submitting', 'true');

    const formData = new FormData(form);
    
    // Handle cart edit context: check if variant changed
    const isCartEdit = this.dataset.cartEdit === 'true';
    const oldVariantId = this.dataset.cartOldVariantId;
    const cartLineKey = this.dataset.cartLineKey;
    const newVariantId = formData.get('id')?.toString();
    
    if (isCartEdit && oldVariantId && cartLineKey && newVariantId && oldVariantId !== newVariantId) {
      // Variant changed: remove old line item first, then add new one
      this.#handleCartEditVariantChange(cartLineKey, formData, form, addToCartTextError || null);
      return;
    }
    
    // Normal flow: same variant (update quantity) or not in cart-edit context
    this.#handleNormalCartAdd(formData, addToCartTextError || null, form, event);
  }

  /**
   * Handles cart edit when variant has changed - removes old item and adds new one
   */
  async #handleCartEditVariantChange(/** @type {string} */ cartLineKey, /** @type {FormData} */ formData, /** @type {HTMLFormElement} */ form, /** @type {HTMLElement | null} */ addToCartTextError) {
    try {
      // Dispatch event to show loading spinner
      document.dispatchEvent(new CustomEvent('cart:updating'));
      
      // First, remove the old line item
      const removeResponse = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: cartLineKey,
          quantity: 0
        })
      });

      if (!removeResponse.ok) {
        throw new Error('Failed to remove old cart item');
      }

      // Then add the new variant (proceed with normal add)
      // Note: event is not available here, but cartPerformance.measureFromEvent will handle it
      this.#handleNormalCartAdd(formData, addToCartTextError, form, null);
    } catch (error) {
      console.error('Error handling cart edit variant change:', error);
      // Reset submitting flag on error
      this.#isSubmitting = false;
      form.removeAttribute('data-submitting');
      
      // Show error to user
      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = 'Failed to update cart item. Please try again.';
        } else {
          const newTextNode = document.createTextNode('Failed to update cart item. Please try again.');
          addToCartTextError.appendChild(newTextNode);
        }
      }
    }
  }

  /**
   * Handles normal cart add (default behavior)
   */
  #handleNormalCartAdd(/** @type {FormData} */ formData, /** @type {HTMLElement | null} */ addToCartTextError, /** @type {HTMLFormElement} */ form, /** @type {Event | null} */ event) {
    
    // Dispatch event to show loading spinner
    document.dispatchEvent(new CustomEvent('cart:updating'));

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          this.#trackEeAddToCart(formData, response);

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
        // Reset submitting flag on error
        this.#isSubmitting = false;
        const form = this.querySelector('form');
        if (form) {
          form.removeAttribute('data-submitting');
        }
      })
      .finally(() => {
        // Reset submitting flag after completion
        this.#isSubmitting = false;
        const form = this.querySelector('form');
        if (form) {
          form.removeAttribute('data-submitting');
        }
        // add more thing to do in here if needed.
        if (event) {
          cartPerformance.measureFromEvent('add:user-action', event);
        }
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId && event.detail.data.productId !== this.dataset.productId) {
      // In quick-add modal context, allow updates if we're in a modal (product might have changed)
      const isInQuickAddModal = this.closest('quick-add-dialog');
      if (!isInQuickAddModal) {
        return;
      }
      // Update product ID to match the event if we're in a modal
      this.dataset.productId = event.detail.data.productId;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton =
      addToCartButtonContainer?.refs.addToCartButton ||
      this.querySelector('button[data-action="back-in-stock"]') ||
      this.querySelector('button[id^="BuyButtons-ProductSubmitButton-"]') ||
      this.querySelector('button[id^="StickyAddToCart-"]');
    if (!currentAddToCartButton) return;

    const matchingButtonId = currentAddToCartButton.id;
    const isStickyAddToCart = Boolean(matchingButtonId && matchingButtonId.startsWith('StickyAddToCart-'));

    const currentAddToCartText = currentAddToCartButton.querySelector('.add-to-cart-text__content');
    const currentLabelText = currentAddToCartText?.textContent?.trim() || '';
    const addToCartBaseLabelCandidate = currentLabelText.includes(' - ')
      ? currentLabelText.split(' - ')[0].trim()
      : currentLabelText || 'Add to cart';
    const addToCartBaseLabel =
      addToCartBaseLabelCandidate.toUpperCase() === 'NOTIFY ME WHEN BACK IN STOCK'
        ? 'Add to cart'
        : addToCartBaseLabelCandidate;

    const matchedSourceButton = matchingButtonId ? event.detail.data.html.getElementById(matchingButtonId) : null;
    let sourceAddToCartButton =
      matchedSourceButton ||
      (!isStickyAddToCart ? event.detail.data.html.querySelector('[ref="addToCartButton"]') : null);

    if (!sourceAddToCartButton && isStickyAddToCart) {
      const stickyFallbackButton = /** @type {HTMLButtonElement} */ (currentAddToCartButton.cloneNode(true));
      const stickyFallbackText = stickyFallbackButton.querySelector('.add-to-cart-text__content');
      if (stickyFallbackText) {
        const priceElement = event.detail.data.html.querySelector(
          'product-price .price-item--sale, product-price .price-item--regular, product-price .price-item'
        );
        const variantPriceText = priceElement?.textContent?.trim() || '';
        stickyFallbackText.textContent = variantPriceText
          ? `${addToCartBaseLabel} - ${variantPriceText}`
          : addToCartBaseLabel;
      }
      sourceAddToCartButton = stickyFallbackButton;
    }
    // Clone the source button so the morph (which is destructive to the source tree)
    // doesn't break other listeners that share the same event.detail.data.html document.
    const newAddToCartButton = sourceAddToCartButton?.cloneNode(true);

    // Clear any lingering "Added" animation so the updated text is visible
    currentAddToCartButton.classList.remove('atc-added');

    // Update the add to cart button text and icon (morph first so we know if we have Back in Stock)
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Use source button to detect Back in Stock (morph may not copy data-action to current button)
    const isBackInStockButton =
      sourceAddToCartButton?.getAttribute('data-action') === 'back-in-stock' ||
      currentAddToCartButton.getAttribute('data-action') === 'back-in-stock';
    /** @param {string} text */
    const setButtonText = (text) => {
      const textNodes = currentAddToCartButton.querySelectorAll('.add-to-cart-text__content');
      if (textNodes.length) {
        textNodes.forEach((node) => {
          node.textContent = text;
        });
      } else {
        currentAddToCartButton.textContent = text;
      }
    };
    /** @param {boolean} disabled */
    const setButtonDisabled = (disabled) => {
      if (addToCartButtonContainer) {
        if (disabled) addToCartButtonContainer.disable();
        else addToCartButtonContainer.enable();
      } else {
        currentAddToCartButton.disabled = disabled;
      }
    };
    if (event.detail.resource == null || event.detail.resource.available == false) {
      // Always force unavailable variants into Back in Stock mode.
      currentAddToCartButton.classList.remove('atc-added');
      setButtonDisabled(false);
      currentAddToCartButton.removeAttribute('disabled');
      currentAddToCartButton.removeAttribute('name');
      currentAddToCartButton.setAttribute('type', 'button');
      currentAddToCartButton.setAttribute('data-action', 'back-in-stock');
      setButtonText('NOTIFY ME WHEN BACK IN STOCK');
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      setButtonDisabled(false);
      currentAddToCartButton.removeAttribute('data-action');
      currentAddToCartButton.setAttribute('type', 'submit');
      currentAddToCartButton.setAttribute('name', 'add');
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      // Safari can ignore dynamic type/attribute updates; re-apply after a tick so button updates correctly
      const ensureAddToCartState = () => {
        if (currentAddToCartButton.getAttribute('data-action') === 'back-in-stock') {
          currentAddToCartButton.removeAttribute('data-action');
        }
        currentAddToCartButton.setAttribute('type', 'submit');
        currentAddToCartButton.setAttribute('name', 'add');
        currentAddToCartButton.removeAttribute('disabled');
      };
      requestAnimationFrame(ensureAddToCartState);
      setTimeout(ensureAddToCartState, 0);
    }

    // Maintain "Update Cart" text if in cart-edit context (add-to-cart only, not Back in Stock)
    if (newAddToCartButton && !isBackInStockButton) {
      const isCartEdit = this.dataset.cartEdit === 'true';
      if (isCartEdit) {
        requestAnimationFrame(() => {
          const addToCartTexts = currentAddToCartButton.querySelectorAll('.add-to-cart-text__content');
          addToCartTexts.forEach(textEl => {
            textEl.textContent = 'Update Cart';
          });
        });
      }
    }

    // Ensure button text is visible after morph (fixes sticky ATC losing text on variant switch)
    requestAnimationFrame(() => {
      // Clear any stale CSS animation states on the text wrapper
      const textWrapper = currentAddToCartButton.querySelector('.add-to-cart-text');
      if (textWrapper) {
        textWrapper.getAnimations().forEach(a => a.cancel());
        textWrapper.style.removeProperty('opacity');
        textWrapper.style.removeProperty('transform');
      }

      // Clear "Added" animation state so it doesn't visually overlap
      const addedWrapper = currentAddToCartButton.querySelector('.add-to-cart-text--added');
      if (addedWrapper) {
        addedWrapper.getAnimations().forEach(a => a.cancel());
      }
    });

    // Update the variant ID
    if (variantId) variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    this.#syncBackInStockDialog(event);
  };

  /**
   * Keeps Back in Stock dialog in sync with current product/variant context.
   * This is required for combined listings where URL/product can change without a full reload.
   * @param {VariantUpdateEvent} event
   */
  #syncBackInStockDialog(event) {
    const currentDialog =
      document.getElementById('back-in-stock-dialog') ||
      document.querySelector('.back-in-stock-dialog');
    if (!(currentDialog instanceof HTMLElement)) return;

    const sourceDialog =
      event.detail.data.html.querySelector('#back-in-stock-dialog') ||
      event.detail.data.html.querySelector('.back-in-stock-dialog');

    // If the fetched HTML has a newer dialog, morph the inner content to refresh title/variants/ids.
    if (sourceDialog instanceof HTMLElement) {
      const currentContent = currentDialog.querySelector('.back-in-stock-dialog__content');
      const sourceContent = sourceDialog.querySelector('.back-in-stock-dialog__content');
      if (currentContent && sourceContent) {
        morph(currentContent, sourceContent.cloneNode(true));
      }
    }

    const activeProductId = event.detail.data.newProduct?.id || this.dataset.productId;
    const bisForm = currentDialog.querySelector('klaviyo-back-in-stock-form');
    if (bisForm && activeProductId) {
      bisForm.setAttribute('data-product', String(activeProductId));
    }

    const variantSelect = currentDialog.querySelector('[data-variant-selector]');
    const activeVariantId = event.detail.resource?.id ? String(event.detail.resource.id) : this.refs.variantId?.value;

    if (variantSelect instanceof HTMLSelectElement && activeVariantId) {
      const hasMatchingVariant = Array.from(variantSelect.options).some((option) => option.value === activeVariantId);
      if (hasMatchingVariant) variantSelect.value = activeVariantId;
    }
  }

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
